import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ImageSelector from "@/components/admin/ImageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    rating: 5,
    text: { fr: "", en: "" },
    circuit_type: "4x4",
    trip_date: "",
    avatar_url: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/testimonials`, getAuthHeaders());
      setTestimonials(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/testimonials/${editingItem.id}`, formData, getAuthHeaders());
        toast.success("Témoignage mis à jour");
      } else {
        await axios.post(`${API}/admin/testimonials`, formData, getAuthHeaders());
        toast.success("Témoignage créé");
      }
      setDialogOpen(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    // Handle both old and new data structures
    const locationValue = typeof item.location === 'object' 
      ? (item.location?.fr || item.location?.en || '') 
      : (item.location || '');
    
    setFormData({
      name: item.name || "",
      location: locationValue,
      rating: item.rating || 5,
      text: item.text || { fr: "", en: "" },
      circuit_type: item.circuit_type || item.trip_type?.fr || "4x4",
      trip_date: item.trip_date || item.date || "",
      avatar_url: item.avatar_url || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, getAuthHeaders());
      toast.success("Témoignage supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleVisible = async (item) => {
    try {
      await axios.put(`${API}/admin/testimonials/${item.id}`, { is_visible: !item.is_visible }, getAuthHeaders());
      toast.success(item.is_visible ? "Témoignage masqué" : "Témoignage affiché");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      location: "",
      rating: 5,
      text: { fr: "", en: "" },
      circuit_type: "4x4",
      trip_date: "",
      avatar_url: "",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold">Témoignages</h1>
          <p className="text-muted-foreground">Gérez les avis clients ({testimonials.length} total)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-testimonial-btn">
              <Plus className="h-4 w-4" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Modifier le témoignage" : "Nouveau témoignage"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Localisation</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Paris, France"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Note</Label>
                  <Select value={String(formData.rating)} onValueChange={(v) => setFormData({ ...formData, rating: parseInt(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[5, 4, 3, 2, 1].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} étoiles</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type de circuit</Label>
                  <Select value={formData.circuit_type} onValueChange={(v) => setFormData({ ...formData, circuit_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4x4">Circuit 4x4</SelectItem>
                      <SelectItem value="trekking">Trekking</SelectItem>
                      <SelectItem value="stage">Stage/Retraite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Date du voyage (optionnel)</Label>
                <Input
                  value={formData.trip_date}
                  onChange={(e) => setFormData({ ...formData, trip_date: e.target.value })}
                  placeholder="Mars 2024"
                />
              </div>
              <div>
                <Label>Commentaire (FR) *</Label>
                <Textarea
                  value={formData.text?.fr || ""}
                  onChange={(e) => setFormData({ ...formData, text: { ...formData.text, fr: e.target.value } })}
                  required
                  rows={4}
                />
              </div>
              <div>
                <Label>Commentaire (EN)</Label>
                <Textarea
                  value={formData.text?.en || ""}
                  onChange={(e) => setFormData({ ...formData, text: { ...formData.text, en: e.target.value } })}
                  rows={4}
                />
              </div>
              <ImageSelector
                label="Avatar (optionnel)"
                value={formData.avatar_url}
                onChange={(url) => setFormData({ ...formData, avatar_url: url })}
              />
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit">{editingItem ? "Mettre à jour" : "Créer"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {testimonials.map((item) => (
          <Card key={item.id} className={`${!item.is_visible ? "opacity-60 border-dashed" : ""}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < item.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`} />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {(item.circuit_type || item.trip_type) && (
                    <Badge variant="outline" className="text-xs">
                      {item.circuit_type === "4x4" || (item.trip_type?.fr || '').includes('4x4') ? "4x4" : 
                       item.circuit_type === "trekking" || (item.trip_type?.fr || '').toLowerCase().includes('trek') ? "Trek" : 
                       "Stage"}
                    </Badge>
                  )}
                  {!item.is_visible && <Badge variant="secondary">Masqué</Badge>}
                </div>
              </div>
              <p className="text-sm mb-4 line-clamp-4 italic">
                "{item.text?.fr || item.text?.en || 'Pas de commentaire'}"
              </p>
              <div className="flex items-center gap-3 mb-4">
                {item.avatar_url ? (
                  <img src={item.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold">{item.name?.charAt(0) || "?"}</span>
                  </div>
                )}
                <div>
                  <p className="font-semibold">{item.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {typeof item.location === 'object' ? (item.location?.fr || item.location?.en || '') : item.location}
                  </p>
                </div>
              </div>
              {(item.trip_date || item.date) && (
                <p className="text-xs text-muted-foreground mb-4">
                  Voyage : {item.trip_date || item.date}
                </p>
              )}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(item)} title="Modifier">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleToggleVisible(item)} title={item.is_visible ? "Masquer" : "Afficher"}>
                  {item.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => handleDelete(item.id)} title="Supprimer">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>Aucun témoignage pour le moment.</p>
          <p className="text-sm mt-2">Cliquez sur "Ajouter" pour créer le premier témoignage.</p>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
